import { ThunkDispatch } from '@reduxjs/toolkit';
import useQueryParams from 'hooks/UseQueryParams';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnyAction } from 'redux';
import { authenticate } from 'store/Auth';
import { RootState } from 'store/store';
import { ThemeProvider } from 'styled-components';
import theme1 from '../../styles/themes/theme1';
import { LOCAL_STORAGE_KEYS } from 'constants/CommonConstants';
import Logger from 'utils/logger';

const withTokenValidation = (WrappedComponent: React.FC, widgetVisibility?: string) => {
  return () => {
    const [theme, setTheme] = useState(theme1);
    const location = useLocation();
    const pathnameParts = location.pathname.split('/');
    const moduleName = pathnameParts[1]; // Extract the
    const hasCalledMounted = React.useRef(false);
    const queryParams = useQueryParams();
    const { token = '' } = useParams();
    const navigate = useNavigate();
    const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

    const [isValid, setIsValid] = useState(false);

    const domain = queryParams.get('domain');
    const type = queryParams.get('type');

    useEffect(() => {
      if (hasCalledMounted.current) return;
      hasCalledMounted.current = true;

      const validateToken = async () => {
        if (window.self !== window.top) {
          document.body.classList.add('initialBg');
        }
        try {
          const authData = { token, domain }; // Create the payload

          dispatch(authenticate(authData)).then((res) => {
            const widgetData = res?.payload?.data?.widgetData;
            const success = res?.payload?.success;

            const isWidgetVisible =
              widgetVisibility && widgetData?.[widgetVisibility]
                ? widgetData?.[widgetVisibility]?.toUpperCase() === 'TRUE'
                : success;

            document.body.classList.remove('initialBg');

            if (isWidgetVisible) {
              const newTheme = {
                colors: {
                  primaryColor: widgetData?.primaryColor || widgetData?.bgColor || '#5e50f1',
                  inputBorder: '#e3e3e3',
                  textColor: '#000',
                  dangerColor: '#d32f2f',
                  checkboxColor: widgetData?.primaryColor || widgetData?.bgColor || '#5e50f1',
                  whiteColor: '#fff',
                  primaryDisabled: '#DCDCDC',
                  lightBorder: '#ccc',
                },
                fonts: {
                  primary: `'Poppins', sans-serif`,
                },
              };

              document.documentElement.style.setProperty(
                '--primary-color',
                widgetData?.primaryColor || widgetData?.bgColor || '#5e50f1'
              );
              setTheme((prev) => {
                const isSame = JSON.stringify(prev) === JSON.stringify(newTheme);
                return isSame ? prev : newTheme;
              });

              setIsValid(true);

              const primaryColor = widgetData?.primaryColor || widgetData?.bgColor;
              const customClassName = widgetData?.customClassName;
              const buttonName = widgetData?.buttonName;
              const success = res?.payload?.success;
              const language = widgetData?.language;
              const widgetName = res?.payload?.data?.widgetName;
              const clientName = res?.payload?.data?.client;
              const banners = res?.payload?.data?.banners || {};
              const accessToken = res?.payload?.data?.accessToken;

              Logger.custom('widget_loaded', {
                widgetName,
                type,
                client: clientName,
                module: moduleName,
              });

              if (type === 'button' || type === 'banner') {
                const moduleName_ = moduleName?.replace(/-([a-z])/g, (_, char) => char?.toUpperCase());

                localStorage.setItem(`${moduleName_}-currentStep`, '0');
                localStorage.removeItem(moduleName_);
                localStorage.removeItem(LOCAL_STORAGE_KEYS.draftLeadId);
              }

              window.parent.postMessage(
                {
                  type: type === 'banner' ? `${moduleName}-modal-open` : `${moduleName}-modal-open-btn`,
                  res: {
                    primaryColor,
                    customClassName,
                    success,
                    buttonName,
                    language,
                    widgetName,
                    type,
                    module: moduleName,
                    clientName,
                    banners,
                    accessToken,
                  },
                },
                '*'
              );
            } else {
              window.parent.postMessage(
                {
                  type: `${moduleName}-modal-close`,
                  data: { close: true },
                  res: { module: moduleName, auth: false, type },
                },
                '*'
              );
            }
          });
        } catch (error) {
          document.body.classList.remove('initialBg');
        }
      };

      validateToken();
    }, [dispatch, token, navigate]);

    // if (isLoading) {
    //   return <CustomOwlLoader show={isLoading} />; // Display a loading spinner while validating the token
    // }

    return isValid ? (
      <ThemeProvider theme={theme}>
        <WrappedComponent />
      </ThemeProvider>
    ) : null;
  };
};

export default withTokenValidation;
